import { IVuHttp } from '../../../services/vu/http/vu-http.interface';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { VuState, ShopState, OrderSaveResult, CashDevicesState } from '../../../lib/lib';
import { SimulatorState } from '../../../reducers/reducers';
import { IVuConnection, IVuConnectionSimulator } from '../../../services/vu/connection/vu-connection.interfaces';
import { ConfigurationService } from '../../../services/configuration/configuration.service';
import { TestingService } from '../../../services/testing.service';
import { VuCommunicationService } from '../../../services/vu/vu-communication.service';
import { SaleService } from '../../../services/sale.service';
import { DispatcherService } from '../../../services/dispatcher.service';
import { UiService } from '../../../services/ui.service';
import { MachinesInactivityService } from '../../../services/machines/machines-inactivity.service';
import { ThemeService } from '../../../services/theme.service';
import { LanguageService } from '../../../services/language.service';
import { CreditCardTerminalSimulatorService } from '../../../services/credit-card-terminal-simulator.service';
import { CookieService } from 'ngx-cookie';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { DictionaryHelper } from '../../../lib/dictionary-helper';
import { LogisticsRequestSimulatorService } from '../../../services/logistics-request/logistics-request-simulator.service';

const SIMULATOR_TAB_INDEX = 'simulator_tab_index';

@Component({
  selector: 'simulator',
  templateUrl: './simulator.component.html',
  styleUrls: ['./simulator.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class SimulatorComponent implements OnInit, OnDestroy {
  private vuSimulator: IVuConnectionSimulator;
  private vuConnection: IVuConnection;
  private vuHttp: IVuHttp;
  isProduction: boolean;
  vuState = new VuState();
  cashDevicesState = new CashDevicesState();
  shopState = new ShopState();
  private subscriptionVuState: Subscription;
  private subscriptionCashDevicesState: Subscription;
  private subscriptionShopState: Subscription;
  simulatorState = new SimulatorState();
  private subscriptionSimulatorState: Subscription;
  cannotPayCash = false;
  cannotPayCard = false;
  isOrderSaving = false;
  tabSelectedIndex = 0;
  shortTab = true;

  constructor(
    protected configurationService: ConfigurationService,
    private testingService: TestingService,
    private vuCommunicationService: VuCommunicationService,
    private saleService: SaleService,
    private dispatcherService: DispatcherService,
    private uiService: UiService,
    private userInactivityService: MachinesInactivityService,
    private themeService: ThemeService,
    private languageService: LanguageService,
    private cookieService: CookieService,
    private logisticsRequestService: LogisticsRequestSimulatorService,
  ) {
    this.vuSimulator = vuCommunicationService.vuConnectionSimulator;
    this.vuConnection = vuCommunicationService.vuConnection;
    this.vuHttp = vuCommunicationService.vuHttp;
    this.logisticsRequestService.generateLogisticsRequestInMemory();
  }

  ngOnInit() {
    this.isProduction = this.configurationService.configuration.isProduction;
    this.subscriptionVuState = this.dispatcherService.onVuStateChangedSubscribe(x => this.vuState = x);
    this.subscriptionCashDevicesState = this.dispatcherService.onCashDevicesStateSubscribe(x => this.cashDevicesState = x);
    this.subscriptionShopState = this.dispatcherService.onShopStateChangedSubscribe(x => this.onShopStateChanged(x));
    this.subscriptionSimulatorState = this.dispatcherService.onSimulatorStateChangedSubscribe(x => this.onSimulatorState(x));
    this.restoreTabSelectedIndex();
  }

  ngOnDestroy() {
    this.subscriptionVuState.unsubscribe();
    this.subscriptionShopState.unsubscribe();
    this.subscriptionSimulatorState.unsubscribe();
    this.subscriptionCashDevicesState.unsubscribe();
  }

  private onShopStateChanged(shopState: ShopState): void {
    this.cannotPayCash = !shopState.canPayCash;
    this.cannotPayCard = !shopState.canPayCard;
    this.isOrderSaving = shopState.isOrderSaving;
    this.shopState = shopState;
  };

  private onSimulatorState(simulatorState: SimulatorState) {
    this.simulatorState.isTestRunning = simulatorState.isTestRunning;
  };

  onMaintenanceModeChange(event: any) {
    this.dispatcherService.vuStateMaintenanceMode(event.currentValue);
  }

  onBurglaryChange(event: any) {
    this.dispatcherService.vuStateBurglaryMode(event.currentValue);
  }

  onOfflineModeChange(event: any) {
    this.dispatcherService.vuStateOfflineMode(event.currentValue);
  }

  onServiceModeClick() {
    this.dispatcherService.vuStateServiceMode(true);
  }

  onCanPayCashChange(event: any) {
    this.dispatcherService.shopStateCanPayCash(!event.currentValue);
  }

  onNoConnectionChange(event: any) {
    this.vuSimulator.connectionChanged(!event.currentValue);
  }

  onTestStartClick() {
    this.testingService.testStart();
  }

  onTestStopClick() {
    this.testingService.testStop();
  }

  onResetMachinesClick() {
    this.dispatcherService.machineHardResetRoot();
  }

  onPaidoutClick() {
    this.vuSimulator.returnAmountFinished();
  }

  onReturnChangeFinishedClick() {
    this.vuSimulator.returnChangeFinished();
  }


  showTimeoutModal() {
    this.userInactivityService.simulateTimeoutModalOpen();
  }

  onOrderSaveClick(isOk: boolean) {
    this.dispatcherService.shopStateIsSavingOrder(false);
    let error = isOk ? '' : 'Error from the simulator';
    let result = new OrderSaveResult(isOk ? 1 : null, this.shopState.orderUid, isOk, error);
    this.vuSimulator.orderSaveResultReceived(result);
  }

  onProductionApiChange(event: any) {
    this.vuHttp.isUseProductionApi = event.currentValue;
  }

  get showDemoBackground(): boolean {
    return this.themeService.showDemoBackground;
  }

  set showDemoBackground(value: boolean) {
    this.themeService.showDemoBackground = value;
  }

  get additionalProperties(): DictionaryHelper {
    return this.configurationService.configuration.additionalPropertiesHelper;
  }

  get liteMode(): boolean {
    return this.additionalProperties.getBoolean("lite_mode");
  }

  set liteMode(value: boolean) {
    this.additionalProperties.setProperty("lite_mode", value);
    this._vuConfigurationChanged();
  }

  get demoMode(): boolean {
    return this.additionalProperties.getBoolean("demo");
  }

  set demoMode(value: boolean) {
    this.additionalProperties.setProperty("demo", value);
    this._vuConfigurationChanged();
  }

  private _vuConfigurationChanged() {
    this.vuCommunicationService.vuConnectionSimulator.configurationChanged(this.configurationService.configuration);
  }

  onTabChanged(event: MatTabChangeEvent) {
    this.cookieService.put(SIMULATOR_TAB_INDEX, event.index.toString());
  }

  restoreTabSelectedIndex() {
    const str = this.cookieService.get(SIMULATOR_TAB_INDEX);
    if (!str) {
      return;
    }
    const tabSelectedIndex = parseInt(str);
    if (isNaN(tabSelectedIndex)) {
      return;
    }

    //TODO: return if tabSelectedIndex is too large.

    this.tabSelectedIndex = tabSelectedIndex;
  }

  onBnaValidationStartedClick() {
    this.vuCommunicationService.vuConnectionSimulator.onBnaValidationStarted();
  }

  onBnaValidationFinishedClick() {
    this.vuCommunicationService.vuConnectionSimulator.onBnaValidationFinished();
  }
}
